import { lazy, useEffect } from 'react'
import { matchPath, Navigate, useParams } from 'react-router-dom'
import Loading from '@features/shared/components/Loading'
import NoAccessScreen from '../features/shared/components/NoAccessScreen'
import { useTables } from '../features/tables/hooks'
import { useQueryErrorToast } from '@features/shared/hooks/useQueryErrorToast'
import { getTableToOpen } from '../features/tables/logic/tableOrdering'
import RedirectKeepingState from '@features/navigation/components/RedirectKeepingState'
import HomeRedirect from '@features/navigation/components/HomeRedirect'
import { useHiddenPagesQuery, useHiddenPaths } from '@features/sidebar/hooks/useHiddenPages'
import { Path } from './paths'
import { MICROSOFT_CALLBACK_PATH } from '@features/integrations/utils/microsoftCallback'

// Kept as a named loader so the table redirects can warm this (large) chunk
// while their tables request is still in flight, instead of after it.
const loadTablesPage = () => import('../features/tables/pages/TablesPage')
const TablesPage = lazy(loadTablesPage)

// The grid's own address, and the redirects that land on it.
const TABLES_PAGE_PATHS = [
  '/tables',
  '/tables/:tableId',
  '/tables/:tableId/table-views/:viewId/records',
]

/**
 * Starts loading the code of the page at `pathname` at once. The router
 * renders only after sign-in and /configuration, so without this a cold load
 * of a table's address downloads the grid after them instead of alongside.
 * Other pages load as they render.
 */
export function preloadPageAt(pathname: string) {
  if (TABLES_PAGE_PATHS.some((path) => matchPath(path, pathname))) void loadTablesPage()
}
const AssistantPage = lazy(() => import('@features/assistant/pages/AssistantPage'))
const FieldMetaPage = lazy(() => import('../features/columns/pages/FieldMetaPage'))
const TableSettingsPage = lazy(() => import('../features/tables/pages/TableSettingsPage'))
const ViewSettingsPage = lazy(() => import('../features/views/pages/ViewSettingsPage'))
const ProfilePage = lazy(() => import('../features/users/pages/ProfilePage'))
const PreferencesPage = lazy(() => import('../features/settings/pages/PreferencesPage'))
const SettingsPage = lazy(() => import('../features/settings/pages/SettingsPage'))

const CredentialsPage = lazy(() => import('@features/integrations/pages/CredentialsPage'))
const PeriodsPage = lazy(() => import('@features/periods/pages/PeriodsPage'))

// Layouts for Settings
const UsersPage = lazy(() => import('../features/permissions/pages/UsersPage'))
const PermissionGroupsPage = lazy(
  () => import('../features/permissions/pages/PermissionGroupsPage')
)
const PreferenceGroupsPage = lazy(() => import('../features/settings/pages/PreferenceGroupsPage'))
const PreferenceGroupDetailPage = lazy(
  () => import('../features/settings/pages/PreferenceGroupDetailPage')
)
const PreferenceDefaultsPage = lazy(
  () => import('../features/settings/pages/PreferenceDefaultsPage')
)
const AutomationsOverviewPage = lazy(() => import('../features/automations/pages/OverviewPage'))
const AutomationsPage = lazy(() => import('../features/automations/pages/AutomationsPage'))
const AutomationDetailPage = lazy(
  () => import('../features/automations/pages/AutomationDetailPage')
)
const AutomationRunsPage = lazy(() => import('../features/automations/pages/RunsPage'))
const RequireAutomationsAccess = lazy(
  () => import('@features/automations/components/RequireAutomationsAccess')
)
const IntegrationsLayout = lazy(
  () => import('@features/integrations/components/IntegrationsLayout')
)
const IntegrationsServerData = lazy(
  () => import('@features/integrations/components/IntegrationsServerData')
)
const IntegrationsOverviewPage = lazy(() => import('../features/integrations/pages/OverviewPage'))
const IntegrationsSourcesPage = lazy(() => import('../features/integrations/pages/SourcesPage'))
const IntegrationsSourceDetailPage = lazy(
  () => import('../features/integrations/pages/SourceDetailPage')
)
const IntegrationsExternalTablesPage = lazy(
  () => import('../features/integrations/pages/ExternalTablesPage')
)
const IntegrationsExternalTablePage = lazy(
  () => import('../features/integrations/pages/ExternalTablePage')
)
const IntegrationsExternalFieldsPage = lazy(
  () => import('../features/integrations/pages/ExternalFieldsPage')
)
const IntegrationsRunsPage = lazy(() => import('../features/integrations/pages/RunsPage'))
const IntegrationsRunDetailPage = lazy(() => import('../features/integrations/pages/RunDetailPage'))
const UserGroupDetailLayout = lazy(
  () => import('../features/permissions/pages/UserGroupDetailLayout')
)
const PermissionsMembershipPage = lazy(
  () => import('../features/permissions/pages/PermissionsMembershipPage')
)
const PermissionsGeneralPage = lazy(
  () => import('../features/permissions/pages/PermissionsGeneralPage')
)
const PermissionsTablesPage = lazy(
  () => import('../features/permissions/pages/PermissionsTablesPage')
)
const PermissionsColumnsPage = lazy(
  () => import('../features/permissions/pages/PermissionsColumnsPage')
)
const PermissionsModulesPage = lazy(
  () => import('../features/permissions/pages/PermissionsModulesPage')
)

// eslint-disable-next-line react-refresh/only-export-components -- redirect helper colocated with the route config that uses it; moving to a separate file would split related route logic.
function TablesIndexRedirect() {
  const { data: tables, isLoading, isError, error } = useTables()
  const { isLoading: isLoadingHidden } = useHiddenPagesQuery()
  const hiddenPaths = useHiddenPaths()
  useQueryErrorToast(isError, error, 'loading tables')
  useEffect(() => {
    void loadTablesPage()
  }, [])

  // Same fallback the lazy route shows, so the spinner stays put until the
  // table workspace mounts instead of a blank area followed by a spinner.
  if (isLoading || isLoadingHidden) return <Loading />
  // Loaded successfully but permission-filtered to zero tables → clear no-access
  // screen instead of rendering TablesPage with no tableId (which loads forever).
  if (!isError && tables && tables.length === 0) {
    return <NoAccessScreen variant="no-tables" />
  }
  // Never a table the user hid from the sidebar, unless every one is hidden.
  const firstTable = tables && getTableToOpen(tables, hiddenPaths)
  if (isError || !firstTable) {
    return <TablesPage />
  }

  return (
    <Navigate
      to={Path.tableDefaultViewRecords(firstTable.id, firstTable.default_view_id)}
      replace
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- redirect helper colocated with the route config that uses it.
function DefaultTableViewRedirect() {
  const { tableId } = useParams<{ tableId: string }>()
  const { data: tables, isLoading, isError } = useTables()
  useEffect(() => {
    void loadTablesPage()
  }, [])

  if (!tableId) {
    return <Navigate to="/" replace />
  }

  if (isLoading) return <Loading />
  if (isError) return <Navigate to={Path.tableDefaultViewRecords(tableId, null)} replace />

  const table = tables?.find((item) => item.id === tableId)
  return <Navigate to={Path.tableDefaultViewRecords(tableId, table?.default_view_id)} replace />
}

// eslint-disable-next-line react-refresh/only-export-components -- redirect helper colocated with the legacy route config that uses it.
function LegacyPermissionGroupRedirect({ tab = 'membership' }: { tab?: 'membership' | 'tables' }) {
  const { userGroupId } = useParams<{ userGroupId: string }>()

  if (!userGroupId) {
    return <Navigate to={Path.usersPermissionGroups} replace />
  }

  return (
    <Navigate
      to={
        tab === 'tables'
          ? Path.usersPermissionGroupTables(userGroupId)
          : Path.usersPermissionGroupMembership(userGroupId)
      }
      replace
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- route wrapper colocated with legacy route config.
function LegacyPreferencesRedirect() {
  const { preferenceTabId } = useParams<{ preferenceTabId?: string }>()
  return (
    <Navigate
      to={preferenceTabId ? Path.preferencesTab(preferenceTabId) : Path.preferences}
      replace
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- route wrapper colocated with the settings route config that uses it.
function SettingsRoute() {
  const { settingsPageKey, settingsTabKey } = useParams<{
    settingsPageKey?: string
    settingsTabKey?: string
  }>()

  return <SettingsPage key={`${settingsPageKey ?? ''}:${settingsTabKey ?? ''}`} />
}

/** Routes using TableLayout - no sidebar, two-row header */
export const tableRoutes = [
  {
    path: '/tables',
    element: <TablesIndexRedirect />,
    // It forwards to the first table: the route picker has the user pick a
    // table, though a home page whose table is gone falls back here (see
    // navigation/logic/routeCatalog).
    handle: { routePicker: { landing: false } },
  },
  {
    path: '/tables/:tableId',
    element: <DefaultTableViewRedirect />,
    // Its address opens the default view, so the route picker shows it as
    // Views › Default.
    handle: { routePicker: { landingIn: 'table-views' } },
  },
  {
    path: '/tables/:tableId/fields/:fieldId/meta',
    element: <Navigate to="overview" replace />,
  },
  {
    path: '/tables/:tableId/fields/:fieldId/meta/:metaTab',
    element: <FieldMetaPage />,
  },
  { path: '/tables/:tableId/table-views/:viewId/records', element: <TablesPage /> },
  {
    path: '/tables/:tableId/table-views/:viewId/settings',
    element: <ViewSettingsPage />,
  },
  {
    path: '/tables/:tableId/settings',
    element: <TableSettingsPage />,
  },
]

/**
 * The app's root address: forwards to the Home Page preference, or to Arvoke
 * AI. Not a page to pick as Home itself.
 */
export const homeRoutes = [
  { path: Path.home, element: <HomeRedirect />, handle: { routePicker: false } },
]

/** Routes using PrivateLayout - full-page AI Assistant chat. A new chat and a
 * stored thread render the same page element, so following a new chat into
 * its thread's route keeps the live conversation mounted. */
export const assistantRoutes = [
  { path: Path.aiChat, element: <AssistantPage /> },
  // A stored conversation, and the address its list falls back to: neither
  // is a page to pick as Home.
  {
    path: `${Path.aiChatThreadsRoot}/:threadId`,
    element: <AssistantPage />,
    handle: { routePicker: false },
  },
  {
    path: Path.aiChatThreadsRoot,
    element: <Navigate to={Path.aiChat} replace />,
    handle: { routePicker: false },
  },
  { path: Path.legacyAiChat, element: <Navigate to={Path.aiChat} replace /> },
]

/** Routes using PrivateLayout - sidebar for settings navigation */
export const settingsRoutes = [
  { path: Path.settingsCredentials, element: <CredentialsPage /> },
  // Microsoft's sign-in comes back to the Credentials page itself, which
  // shows its card Connecting (useMicrosoftCallback): never a page of its own.
  {
    path: MICROSOFT_CALLBACK_PATH,
    element: <CredentialsPage />,
    handle: { routePicker: false },
  },
  // Periods isn't a Settings catalog page: its own tabs, read and written through /periods.
  {
    path: Path.settingsPeriods,
    element: <PeriodsPage key="fiscal" tab="fiscal" />,
    handle: { routePicker: { landing: 'First tab' } },
  },
  { path: Path.settingsPeriodsPayroll, element: <PeriodsPage key="payroll" tab="payroll" /> },
  {
    path: Path.settingsPeriodsTimesheets,
    element: <PeriodsPage key="timesheets" tab="timesheets" />,
  },
  { path: Path.settingsPeriodsHolidays, element: <PeriodsPage key="holidays" tab="holidays" /> },
  { path: Path.settingsPeriodsCustom, element: <PeriodsPage key="custom" tab="custom" /> },
  {
    path: '/me/profile',
    element: <ProfilePage />,
  },
  {
    path: '/me/preferences',
    element: <PreferencesPage />,
    handle: { routePicker: { landing: 'First tab' } },
  },
  {
    path: '/me/preferences/:preferenceTabId',
    element: <PreferencesPage />,
  },
  {
    path: '/profile',
    element: <Navigate to={Path.profile} replace />,
  },
  {
    path: '/preferences',
    element: <Navigate to={Path.preferences} replace />,
  },
  {
    path: '/preferences/:preferenceTabId',
    element: <LegacyPreferencesRedirect />,
    // An old address; the route picker leaves it out.
    handle: { routePicker: false },
  },
  {
    path: '/settings',
    element: <Navigate to={Path.settingsOrganization} replace />,
  },
  {
    path: '/settings/general',
    element: <Navigate to={Path.settingsOrganization} replace />,
  },
  {
    path: '/settings/:settingsPageKey',
    element: <SettingsRoute />,
    handle: { routePicker: { landing: 'First tab' } },
  },
  {
    path: '/settings/:settingsPageKey/:settingsTabKey',
    element: <SettingsRoute />,
  },
  {
    path: '/settings/permissions/users',
    element: <Navigate to={Path.users} replace />,
  },
  {
    path: '/settings/permissions/user-groups',
    element: <Navigate to={Path.usersPermissionGroups} replace />,
  },
  // Old addresses that redirect; the route picker leaves them out.
  {
    path: '/settings/permissions/user-groups/:userGroupId',
    element: <LegacyPermissionGroupRedirect />,
    handle: { routePicker: false },
  },
  {
    path: '/settings/permissions/user-groups/:userGroupId/membership',
    element: <LegacyPermissionGroupRedirect />,
    handle: { routePicker: false },
  },
  {
    path: '/settings/permissions/user-groups/:userGroupId/records',
    element: <LegacyPermissionGroupRedirect tab="tables" />,
    handle: { routePicker: false },
  },
]

/** Routes using PrivateLayout - sidebar for users navigation */
export const usersRoutes = [
  { path: Path.usersRoot, element: <Navigate to={Path.users} replace /> },
  // A grid page's address names its view (see gridViewPath); its own address
  // opens the view it was last on.
  {
    path: Path.users,
    element: <UsersPage />,
    handle: { routePicker: { landing: 'Default' } },
  },
  { path: '/users/users/views/:viewId', element: <UsersPage /> },
  {
    path: '/users/permission-groups',
    element: <PermissionGroupsPage />,
    handle: { routePicker: { landing: 'All groups' } },
  },
  { path: '/users/permission-groups/views/:viewId', element: <PermissionGroupsPage /> },
  {
    path: '/users/preference-groups',
    element: <PreferenceGroupsPage />,
    handle: { routePicker: { landing: 'All groups' } },
  },
  { path: '/users/preference-groups/views/:viewId', element: <PreferenceGroupsPage /> },
  {
    path: '/users/preference-groups/:preferenceGroupId',
    element: <PreferenceGroupDetailPage />,
    handle: { routePicker: { landing: 'Members' } },
  },
  {
    path: '/users/preference-groups/:preferenceGroupId/tabs/:preferenceTabId',
    element: <PreferenceGroupDetailPage />,
  },
  {
    path: '/users/preference-defaults',
    element: <PreferenceDefaultsPage />,
    handle: { routePicker: { landing: 'First tab' } },
  },
  {
    path: '/users/preference-defaults/:preferenceTabId',
    element: <PreferenceDefaultsPage />,
  },
  {
    path: '/users/permission-groups/:userGroupId',
    element: <UserGroupDetailLayout />,
    children: [
      { index: true, element: <Navigate to="membership" replace /> },
      { path: 'membership', element: <PermissionsMembershipPage /> },
      { path: 'general', element: <PermissionsGeneralPage /> },
      { path: 'tables', element: <PermissionsTablesPage /> },
      { path: 'columns', element: <PermissionsColumnsPage /> },
      { path: 'modules', element: <PermissionsModulesPage /> },
      { path: 'records', element: <Navigate to="../tables" replace /> },
    ],
  },
]

/** Routes using PrivateLayout - sidebar for automations navigation */
/** Automations' pages, for people with the Automations module permission. */
export const automationsRoutes = [
  {
    element: <RequireAutomationsAccess />,
    children: [
      { path: Path.automationsRoot, element: <Navigate to={Path.automationsOverview} replace /> },
      { path: Path.automationsOverview, element: <AutomationsOverviewPage /> },
      {
        path: Path.automations,
        element: <AutomationsPage />,
        handle: { routePicker: { landing: 'All automations' } },
      },
      { path: '/automations/automations/views/:viewId', element: <AutomationsPage /> },
      // Starting an automation is an action, not a page to go back to.
      {
        path: Path.automationNew,
        element: <AutomationDetailPage mode="create" />,
        handle: { routePicker: false },
      },
      {
        path: '/automations/automations/:automationId',
        element: <RedirectKeepingState to="configurations" />,
      },
      {
        path: '/automations/automations/:automationId/configurations',
        element: <AutomationDetailPage />,
      },
      {
        path: '/automations/automations/:automationId/versions',
        element: <AutomationDetailPage />,
      },
      {
        path: '/automations/automations/:automationId/runs',
        element: <AutomationDetailPage />,
        handle: { routePicker: { landing: 'Default' } },
      },
      {
        path: '/automations/automations/:automationId/runs/views/:viewId',
        element: <AutomationDetailPage />,
      },
      {
        path: Path.automationRuns,
        element: <AutomationRunsPage />,
        handle: { routePicker: { landing: 'Default' } },
      },
      { path: '/automations/runs/views/:viewId', element: <AutomationRunsPage /> },
    ],
  },
]

/**
 * Routes using PrivateLayout - sidebar for integration navigation. Every page sits
 * in IntegrationsLayout, which holds the one save bar and leave guard over all of
 * Integrations' unsaved configuration, under the server's configuration and runs.
 */
export const integrationsRoutes = [
  {
    element: (
      <IntegrationsServerData>
        <IntegrationsLayout />
      </IntegrationsServerData>
    ),
    children: [
      { path: Path.integrationsRoot, element: <Navigate to={Path.integrationsOverview} replace /> },
      { path: Path.integrationsOverview, element: <IntegrationsOverviewPage /> },
      {
        path: Path.integrationsSources,
        element: <IntegrationsSourcesPage />,
        handle: { routePicker: { landing: 'All sources' } },
      },
      { path: '/integrations/sources/views/:viewId', element: <IntegrationsSourcesPage /> },
      {
        path: '/integrations/sources/:connectionId',
        element: <RedirectKeepingState to="settings" />,
      },
      {
        path: '/integrations/sources/:connectionId/tables',
        element: <IntegrationsSourceDetailPage />,
        handle: { routePicker: { landing: 'Default' } },
      },
      {
        path: '/integrations/sources/:connectionId/tables/views/:viewId',
        element: <IntegrationsSourceDetailPage />,
      },
      {
        path: '/integrations/sources/:connectionId/fields',
        element: <IntegrationsSourceDetailPage />,
        handle: { routePicker: { landing: 'Default' } },
      },
      {
        path: '/integrations/sources/:connectionId/fields/views/:viewId',
        element: <IntegrationsSourceDetailPage />,
      },
      {
        path: '/integrations/sources/:connectionId/settings',
        element: <IntegrationsSourceDetailPage />,
      },
      {
        path: '/integrations/sources/:connectionId/runs',
        element: <IntegrationsSourceDetailPage />,
        handle: { routePicker: { landing: 'Default' } },
      },
      {
        path: '/integrations/sources/:connectionId/runs/views/:viewId',
        element: <IntegrationsSourceDetailPage />,
      },
      {
        path: Path.integrationsTables,
        element: <IntegrationsExternalTablesPage />,
        handle: { routePicker: { landing: 'All tables' } },
      },
      { path: '/integrations/tables/views/:viewId', element: <IntegrationsExternalTablesPage /> },
      { path: '/integrations/tables/:tableId', element: <RedirectKeepingState to="settings" /> },
      {
        path: '/integrations/tables/:tableId/settings',
        element: <IntegrationsExternalTablePage />,
      },
      {
        path: '/integrations/tables/:tableId/fields',
        element: <IntegrationsExternalTablePage />,
        handle: { routePicker: { landing: 'Default' } },
      },
      {
        path: '/integrations/tables/:tableId/fields/views/:viewId',
        element: <IntegrationsExternalTablePage />,
      },
      {
        path: Path.integrationsFields,
        element: <IntegrationsExternalFieldsPage />,
        handle: { routePicker: { landing: 'Default' } },
      },
      { path: '/integrations/fields/views/:viewId', element: <IntegrationsExternalFieldsPage /> },
      {
        path: Path.integrationsRuns,
        element: <IntegrationsRunsPage />,
        handle: { routePicker: { landing: 'Default' } },
      },
      { path: '/integrations/runs/views/:viewId', element: <IntegrationsRunsPage /> },
      {
        path: '/integrations/runs/:runId',
        element: <IntegrationsRunDetailPage />,
        handle: { routePicker: { landing: 'Default' } },
      },
    ],
  },
]
